import { useState, useCallback, useEffect, useRef } from "react";
import { Outlet } from "react-router-dom";
import { authClient } from "../lib/auth-client";
import { MembershipProvider } from "../contexts/MembershipContext";
import { WelcomeModal } from "../components/Welcome/WelcomeModal";
import { useTourSettings } from "../hooks/useTourSettings";
import { useAnalytics } from "../hooks/useAnalytics";
import { isNative } from "../lib/platform";
import {
  logAppsFlyerEvent,
  setAppsFlyerCustomerUserId,
  takeSignupMethod,
} from "../lib/appsflyer";
import NativePaywall from "../components/Membership/NativePaywall";
import { useSeedWatchlist } from "../components/Dashboard/Watchlists/hooks/useSeedWatchlist";
import {
  clearOnboardingSignupPending,
  getCompletedOnboardingAnswers,
} from "../components/Native/onboarding/onboardingStorage";

/**
 * Protected route that wraps all app routes with MembershipProvider.
 *
 * Flow for MEMBERS:
 * - Allow full, unrestricted access to all routes
 *
 * Flow for NON-MEMBERS (free tier):
 * - Web: if welcome modal not dismissed → show dismissible WelcomeModal,
 *   then allow access with free tier restrictions (enforced by individual
 *   components via useMembership()).
 * - Native: HARD paywall — non-members always see the paywall instead of the
 *   app (docs/mobile-onboarding-plan.md §6.2). This is also what makes the
 *   onboarding flow continue straight from auth into the paywall.
 */
export const ActiveMembershipProtectedRoute = () => {
  const { data, isPending } = authClient.useSession();
  const isMembershipActive = data?.session?.is_membership_active ?? false;
  const userId = data?.user?.id;

  const {
    completeTour,
    isCompleted: isTourCompleted,
    isLoading: isTourLoading,
  } = useTourSettings();

  const { capture } = useAnalytics();

  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  const onboardingRecorded = useRef(false);
  // Separate from onboardingRecorded, which is deliberately reset when
  // completeTour() fails so the flag is retried on a later visit. Attribution
  // must NOT be retried: a second af_complete_registration would inflate the
  // conversion count the ad networks optimize toward.
  const registrationLogged = useRef(false);
  const { mutate: seedWatchlist } = useSeedWatchlist();

  // Pin AppsFlyer's customer user id on EVERY authenticated launch, not just
  // at registration. It is the key that joins AppsFlyer's raw data to our user
  // (the same id RevenueCat and PostHog use) and setting it is idempotent, so
  // gating it behind the once-per-account registration block left it unset for
  // returning users, reinstalls, new devices, and anyone who signed up on web.
  // The registration EVENT below stays once-per-account; the id does not.
  useEffect(() => {
    if (!isNative() || !userId) return;
    void setAppsFlyerCustomerUserId(userId);
  }, [userId]);

  // First authenticated visit after the native onboarding quiz: record the
  // durable cross-device "has been onboarded" flag, attach the quiz answers
  // to the PostHog person, seed the starting watchlist from their market
  // interests, and clear the local route-to-signup hint. The web
  // WelcomeModal and other devices skip onboarding from here on.
  useEffect(() => {
    if (!isNative() || isPending || isTourLoading) return;
    clearOnboardingSignupPending();
    if (onboardingRecorded.current || isTourCompleted) return;
    const answers = getCompletedOnboardingAnswers();
    if (!answers) return;
    onboardingRecorded.current = true;
    // Attribution: pin AppsFlyer to the same user id RevenueCat and PostHog
    // use, then log the registration once — this effect is the only place
    // gated on a first authenticated visit (server flag + stored answers),
    // which is exactly registration semantics.
    if (!registrationLogged.current) {
      registrationLogged.current = true;
      logAppsFlyerEvent("af_complete_registration", {
        af_registration_method: takeSignupMethod() ?? "unknown",
      });
    }
    capture("onboarding_profile_set", {
      $set: {
        onboarding_experience: answers.experience,
        onboarding_goal: answers.goal,
        onboarding_decide: answers.decide,
        onboarding_struggle: answers.struggle,
        onboarding_time: answers.time,
        onboarding_markets: answers.markets,
      },
    });
    // Best-effort: the server no-ops for anyone who already has a watchlist,
    // and a failure here must never block the session or tour stamping.
    seedWatchlist(answers.markets ?? [], {
      onSuccess: (result) =>
        capture("watchlist_seeded", {
          markets: answers.markets ?? [],
          seeded: result.seeded,
          ticker_count: result.tickers?.length ?? 0,
        }),
    });
    completeTour().catch(() => {
      // Non-fatal: the flag is recorded on a later visit instead
      onboardingRecorded.current = false;
    });
  }, [isPending, isTourLoading, isTourCompleted, completeTour, capture, seedWatchlist, userId]);

  const handleWelcomeDismiss = useCallback(async () => {
    try {
      await completeTour();
      capture("tour_completed", { completion_source: "welcome_modal" });
      setWelcomeDismissed(true);
    } catch (error) {
      console.error("Failed to save welcome modal dismissal:", error);
      setWelcomeDismissed(true);
    }
  }, [completeTour, capture]);

  if (isPending || isTourLoading) {
    return null;
  }

  // Native hard paywall: non-members never browse the app.
  if (isNative() && !isMembershipActive) {
    return (
      <MembershipProvider>
        <NativePaywall />
      </MembershipProvider>
    );
  }

  const shouldShowWelcome =
    !isMembershipActive && !isTourCompleted && !welcomeDismissed;

  return (
    <MembershipProvider>
      {shouldShowWelcome && (
        <WelcomeModal open={shouldShowWelcome} onDismiss={handleWelcomeDismiss} />
      )}
      <Outlet />
    </MembershipProvider>
  );
};
